import { useState } from 'react'
import { dateText, shortDue, daysUntil } from '../lib/when'
import { formatPhoneDisplay } from '../lib/phone'
import { CONVERSATION, chasingPillFor, deliveredCounts } from '../lib/chase'
import ChaseChips from './ChaseChips'
import Icon from './Icon'
import * as api from '../lib/api'
import { formatRupees } from '../lib/money'
import SampleTag from './SampleTag'
import { isSampleCode } from '../lib/samples'

/**
 * One client's line, on whichever screen is showing it.
 *
 * Extracted from the Ledger Collector so the test bench shows the *same* row
 * rather than a lookalike — the whole point of the bench is watching this row
 * change as the real flow runs, and a second copy of the markup would drift from
 * this one within a week and quietly stop proving anything.
 *
 * Everything it needs is passed in. It reads no API and holds no state beyond a
 * button's own "sending…", so both callers keep their own loading and refresh.
 *
 * @param {object} p
 * @param {object} p.request         the ledger request document
 * @param {string} p.clientName      display name (the ledger's, which is fuller than ours)
 * @param {object} [p.balance]       `{ side, balancePaise }` from Track, when the screen has it
 * @param {string|null} [p.whatsAppNo] this client's number, or null when there
 *   is none on file — undefined (the test bench, which has its own contact by
 *   construction) skips the column entirely rather than claiming "no number".
 * @param {string|null} [p.email]     same rule as `whatsAppNo`: null means "on
 *   file and empty", undefined means "this caller has no opinion, skip it".
 * @param {import('react').ReactNode|false} [p.secondCell]
 *   Replaces the balance cell outright, or `false` to drop that column entirely.
 *   The test bench has no Track account to show a balance for, and an empty money
 *   column there would read as a client who owes nothing rather than one Track
 *   has never heard of. A caller that drops it must drop the header too — `cols`
 *   is the contract between them.
 * @param {string} p.cols            the grid template — the header above it must use the same
 * @param {Function} p.onOpen        row clicked
 * @param {Function} p.onWhatsApp    the WhatsApp chip or the status pill
 * @param {Function} [p.onEmail]     the email cell — same drawer as `onWhatsApp`, the
 *   thread is not filtered per channel. Optional: a caller with no email column
 *   (the test bench passes no `email` prop at all) simply never renders it.
 * @param {Function} p.onView        `({ side })` — open a ledger
 * @param {boolean} [p.selected]      checkbox state — omit `onToggleSelect` to drop the checkbox entirely
 * @param {Function} [p.onToggleSelect] checkbox toggled; its presence is what turns the checkbox on
 * @param {boolean} [p.autoChasing]   this client is on the daily scheduled chase list —
 *   a fact from the server, independent of `selected`, which is only this page's own
 *   transient pick for the next bulk click
 */
export default function CollectionRow({
  request: r,
  clientName,
  balance = null,
  whatsAppNo,
  email,
  secondCell = null,
  cols,
  onOpen,
  onWhatsApp,
  onEmail,
  onView,
  selected = false,
  onToggleSelect,
  autoChasing = false,
  selectable = true,
}) {
  const theirCount = r.theirSide?.entryCount ?? 0
  const untilDue = daysUntil(r.nextDueOn)
  const convoState = r.conversation?.state ?? 'NOT_STARTED'
  /*
   * A client with neither a WhatsApp number nor an email on file cannot be
   * chased at all — no server-side state exists for that (`conversation.state`
   * is derived purely from the attempt log, and there is nothing to attempt),
   * so it is computed here from the two contact props the row already has,
   * the same ones the WhatsApp and email cells below read. Only overrides the
   * label while the server's own state is still one of the two "nothing has
   * happened yet" states — a client who was reachable, got chased, and later
   * lost their contact info on file still shows what actually happened
   * (`PROMISED`, `DISPUTED`, whatever it was), not a blank "no contact" that
   * would erase it.
   */
  const noContact =
    whatsAppNo !== undefined && email !== undefined && !whatsAppNo && !email &&
    (convoState === 'NOT_STARTED' || convoState === 'AWAITING_REPLY')
  /*
   * After a reconciliation found things for them to correct, the row is about
   * that chase instead: how many times we've asked, or — after 7 without
   * their ledger — that it has stopped and needs someone to call them.
   */
  const postReco = r.postReco
  const pill = postReco?.refused
    ? { label: 'Refused — chase stopped', attention: true }
    : postReco?.needsPerson
    ? { label: 'Needs a person — 7 asks, no ledger', attention: true }
    : postReco?.promisedUntil
      ? { label: `Promised by ${dateText(postReco.promisedUntil)} — waiting`, attention: false }
      : postReco
        ? { label: `Asked to correct — ${postReco.asks} of 7`, attention: false }
      : noContact
        ? CONVERSATION.NO_CONTACT
        : chasingPillFor(convoState)
  /*
   * Their statement is in hand — rows extracted, or a filed document to open.
   * This is what puts a View in the "Their statement" cell, and it has to hold
   * before anybody has judged the file: reading it is how you judge it.
   */
  const theirCollected = theirCount > 0 || Boolean(r.theirSide?.docId)
  const L = balance ?? {}
  const chasingSince =
    r.requestedAt && !r.theirSide?.receivedAt
      ? new Date(r.requestedAt).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'Asia/Kolkata',
        })
      : null

  return (
    <>
      {onToggleSelect && (
        // Outside the grid, not a ninth column — a checkbox is a row-level
        // action, not a fact about the client, and adding it as a real column
        // would mean threading a matching cell through every other caller of
        // this row (the test bench has no selection at all) and through the
        // skeleton placeholder's column count. `:has(.rwsel)` in the CSS is
        // what turns the row into a flex box for it.
        <label
          className={`rwsel${selectable ? '' : ' off'}`}
          onClick={(e) => e.stopPropagation()}
          title={selectable ? undefined : 'No WhatsApp number or email on file — add one before this client can be chased'}
        >
          <input
            type="checkbox"
            checked={selectable && selected}
            disabled={!selectable}
            onChange={onToggleSelect}
            aria-label={`Select ${clientName || r.clientCode}`}
          />
        </label>
      )}
      <div
        className="rowgrid rowbtn"
        style={{ gridTemplateColumns: cols }}
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
      >
        <span>
          <span className="c-name" title={clientName || r.clientCode}>{clientName || r.clientCode}</span>
          <span className="c-sub">
            {r.clientCode}
            <SampleTag show={isSampleCode(r.clientCode)} />
            {autoChasing && (
              <span className="c-autochase" title="On the daily scheduled chase list">
                · auto-chase
              </span>
            )}
          </span>
        </span>

        {/*
          The live balance from Track. `side` carries the direction and
          the amount stays positive — "Cr ₹64,20,986" reads correctly
          where a bare negative leaves the reader working out whose
          favour it is in.
        */}
        {/*
          Paise always shown, not just when they are non-zero.
          Two reasons, and they point the same way. `money.js` says
          outright not to hide them in a table an accountant reconciles
          against — a concealed 66 paise is a difference nobody can see.
          And a column where only some rows carry `.66` cannot line up:
          right-aligning pushes the rupee digits of those rows two
          characters left of every other row, so the figures no longer
          read down the column. `.00` on the round ones is the price of
          both, and it is the cheaper mistake.
        */}
        {secondCell === false ? null : secondCell !== null ? (
          <span>{secondCell}</span>
        ) : (
        <span className="bal">
          {L.side ? (
            <span className={`bal-amt ${L.side === 'Dr' ? 'dr' : 'cr'}`}>{formatRupees(L.balancePaise)}</span>
          ) : (
            <span className="mute">—</span>
          )}
        </span>
        )}

        {/*
          A button when there is something to open, and nothing at all
          when there isn't. Every fact that used to be printed here — when
          it arrived, how, how many rows, whether our side adds up — is
          either in the panel or in the banner above the table.
        */}
        <span>
          {!theirCollected ? (
            <span className="mute">—</span>
          ) : theirCount > 0 ? (
            // Extracted rows exist — open them in the table view.
            <FileButton rows={theirCount} onOpen={() => onView({ side: 'CLIENT' })} />
          ) : (
            // A filed document with no rows (a WhatsApp file we do not
            // extract) — View downloads the file. Gated on docId, not the
            // received flag: "received" without a document is nothing to open.
            <StatementButton request={r} />
          )}
        </span>

        <span>
          {/*
            No longer a way into the thread — see the contact cell below.
            Both the chip and this pill are read-only now: one look tells you
            how many times and what state, and there is exactly one place on
            the row that opens the drawer, so nobody has to guess which of
            two lookalike controls actually does something.
          */}
          <ChaseChips counts={deliveredCounts(r.chaseCounts, r.failedCounts)} />
          <span className={`rowstate${pill.attention ? ' attn' : ''}`}>
            {pill.label}
            {r.conversation?.unread > 0 && <span className="rowstate-n">{r.conversation.unread}</span>}
          </span>
          {/* A dispute no longer pauses the chase — it's a flag for a person. */}
          {r.conversation?.disputed && <span className="rowstate attn" title="They disputed a figure — chase continues">Disputed</span>}
          {/*
            How long this ask has been open — from the first message actually
            sent (`requestedAt`), not from when the row was created. Only while
            it's still outstanding: once their statement is in, the chase is
            over and the date stops meaning anything. This screen only ever
            shows each client's current ask, so it is always the latest one.
          */}
          {chasingSince && (
            <span className="c-sub" title={`First asked ${chasingSince}`}>
              Chasing since {chasingSince}
            </span>
          )}
        </span>

        {whatsAppNo !== undefined && (
          <span>
            {/*
              Whether this client can be reached on WhatsApp at all, and the
              number itself when it can — one of two facts Track's balance and
              name say nothing about, and the reason a row can sit untouched
              with nobody able to explain why: there was never a number to try.
              Now also the one thing on the row that opens the thread —
              clicking it works even on "no number", since the drawer is
              exactly where somebody would go to find out why there isn't one.
            */}
            <span
              className={`c-sub contact contact-btn${whatsAppNo ? '' : ' none'}`}
              role="button"
              tabIndex={0}
              title="Open the WhatsApp thread"
              onClick={(e) => {
                e.stopPropagation()
                onWhatsApp()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onWhatsApp()
                }
              }}
            >
              {whatsAppNo ? formatPhoneDisplay(whatsAppNo) : 'no number'}
            </span>
          </span>
        )}

        {email !== undefined && (
          <span>
            {/* The other reachability fact, same rule as the number beside it —
                and the channel that actually covers more of the list: more
                collectible parties have an email on file than a WhatsApp
                number. Clickable the same way now that email chases are a
                real send path — opens the same drawer either channel does,
                since the thread there is never filtered per channel. */}
            <span
              className={`c-sub contact contact-btn${email ? '' : ' none'}`}
              role="button"
              tabIndex={0}
              title={email || 'Open the activity thread'}
              onClick={(e) => {
                e.stopPropagation()
                onEmail?.()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onEmail?.()
                }
              }}
            >
              {email || 'no email'}
            </span>
          </span>
        )}

        {/*
          The next ledger only appears once the current one is in — there
          is no "next" to plan while we are still collecting this one, and
          a due date shown beside a client who has sent nothing reads as a
          schedule nobody is keeping. Until then the cell says what we are
          actually waiting on. Purely informational now — every client is
          asked monthly, with nothing left to configure per row.
        */}
        {(() => {
          const received = Boolean(r.theirSide?.receivedAt)
          /*
            The chase the server says is due, which is a different question from
            when the *next* collection cycle starts. Only set while their
            statement is still outstanding — after that this cell is about the
            schedule again.
          */
          const dueDay = !received ? (r.chaseDueOn ?? null) : null
          return (
            <div className="chasecell">
              {!received ? (
                /*
                  "Awaiting current" is only honest while we are actually waiting
                  on the client. A row whose next chase is due today is waiting on
                  *us*, and reading it as "awaiting" is how a due row goes
                  untouched — so it says which, and when.
                */
                dueDay ? (
                  <>
                    <span className="due">{dateText(dueDay)}</span>
                    <span className={`due-sub${daysUntil(dueDay) < 0 ? ' over' : ' soon'}`}>
                      {daysUntil(dueDay) <= 0 ? 'chase due' : shortDue(dueDay)}
                    </span>
                  </>
                ) : (
                  <span className="mute">{String(r._id).startsWith('ledger-') ? '—' : 'Nothing due'}</span>
                )
              ) : r.nextDueOn ? (
                <>
                  <span className="due">{dateText(r.nextDueOn)}</span>
                  <span className={`due-sub${untilDue < 0 ? ' over' : untilDue <= 7 ? ' soon' : ''}`}>
                    {shortDue(r.nextDueOn)}
                  </span>
                </>
              ) : (
                <span className="mute">Not scheduled</span>
              )}
            </div>
          )
        })()}
      </div>
    </>
  )
}

/**
 * View for a received statement that has no extracted rows.
 *
 * A WhatsApp statement is a filed document, not a table — so "View" opens the
 * file rather than an empty rows grid. Downloads through the backend proxy, the
 * only path that can read DoubleTick's authenticated media host.
 */
function StatementButton({ request }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  return (
    <button
      className="btn btn-sm"
      disabled={busy}
      title="Open the received statement"
      onClick={async (e) => {
        e.stopPropagation()
        setBusy(true)
        setErr(null)
        try {
          await api.downloadStatement(request._id, `${request.clientCode}-statement`)
        } catch (x) {
          setErr(x.message)
        } finally {
          setBusy(false)
        }
      }}
    >
      <Icon name="doc" size={13} />
      {busy ? '…' : err ? 'Retry' : 'View'}
    </button>
  )
}

/**
 * One button, or nothing.
 *
 * Nothing is deliberate. A greyed-out "View" on every row where there is no file
 * is a column of dead controls, and reads as something being broken; an empty cell
 * reads as "not yet", which is what it means.
 */
function FileButton({ rows, onOpen }) {
  if (!rows) return <span className="mute">—</span>
  return (
    <button
      className="btn btn-sm"
      onClick={(e) => {
        e.stopPropagation()
        onOpen()
      }}
      title={`Open ${rows} rows`}
    >
      <Icon name="eye" size={13} />
      View
    </button>
  )
}
